import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { AuthShell } from './AuthShell'
import { Button, TextField } from '@/components/layout/ui'
import { authErrorMessage, authLinkClass, FormError, PasswordField } from '@/features/auth'
import { fieldErrorFor } from '@/lib/errors'
import { useRegister } from '@/lib/session'

const FIELDS = ['name', 'email', 'password', 'password_confirmation']

/**
 * Create an account.
 *
 * The account is new here even for someone who has used Taskly for a year: the
 * web app has its own store, so an iPad sign-in does not resolve on this side.
 * The copy says so plainly rather than letting someone try their iPad password
 * three times and conclude the product is broken.
 *
 * Registration provisions the personal workspace, its owner membership and the
 * first project in one server-side transaction, so the redirect lands on a
 * workspace that already exists rather than an empty shell.
 */
export default function RegisterPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const register = useRegister()

  // Carried through from an invitation link, so signing up resumes the join.
  const from = (location.state as { from?: string } | null)?.from

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    register.mutate(
      {
        name: name.trim(),
        email: email.trim(),
        password,
        password_confirmation: confirmation,
      },
      { onSuccess: () => navigate(from ?? '/', { replace: true }) },
    )
  }

  const error = register.error

  return (
    <AuthShell
      title="Create your Taskly account"
      description="This is your account for Taskly on the web. The iPad app keeps its own sign-in."
      footer={
        <>
          Already have a web account?{' '}
          <Link to="/login" state={from ? { from } : undefined} className={authLinkClass}>
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <TextField
          label="Name"
          name="name"
          autoComplete="name"
          autoFocus
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrorFor(error, 'name')}
        />
        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="off"
          spellCheck={false}
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrorFor(error, 'email')}
        />
        <PasswordField
          label="Password"
          name="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrorFor(error, 'password')}
        />
        <PasswordField
          label="Confirm password"
          name="password_confirmation"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          error={fieldErrorFor(error, 'password_confirmation')}
        />

        <FormError message={authErrorMessage(error, FIELDS, 'sign-up attempts')} />

        <Button type="submit" variant="primary" className="w-full" loading={register.isPending}>
          {register.isPending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  )
}
